<script lang="ts">
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import { Paperclip, Download, Trash2, Plus, Lock } from "lucide-svelte"
  import Form from "./Form.svelte"
  import { xhr, toast } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  const plan: PlanType = appStore.plan
  let {
    id,
    type
  }: {
    id: number
    type: string
  } = $props()

  const editable: boolean = appStore.editable || appStore.update_goals
  const freePlan: boolean = appStore.subscription.subscription_type == "free"
  let attachments: AttachmentType[] = $state([])

  let loading = $state(true)

  async function getAttachments(): Promise<void> {
    try {
      const resp = await xhr.get(
        `/plans/${plan.id}/attachments?id=${id}&type=${type}`
      )

      attachments = resp.data.attachments.map((p: any) => {
        return { ...p, edit: false }
      }) as AttachmentType[]
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the attachments", "error")
    } finally {
      attachments = attachments
      loading = false
    }
  }

  // Create progress report
  let newAttachment = $state(false)
  let editAttachment = $state(false)
  let attachment = $state(null)

  // Delete progress
  async function deleteAttachment(attachment: any): Promise<void> {
    attachment.marked = true
    attachments = attachments
    setTimeout(async () => {
      if (confirm("Are you sure you want to delete the attachment?")) {
        try {
          await xhr.delete(`/plans/${plan.id}/attachments/${attachment.id}`)
          attachments = attachments
        } catch (e) {
          if (typeof Honeybadger.notify !== "undefined") {
            Honeybadger.notify(e)
          }
          toast("There was an error deleting the attachment", "error")
        } finally {
          attachment.marked = false
          const idx = attachments.findIndex((d) => d.id === attachment.id)
          attachments.splice(idx, 1)
          attachments = attachments
        }
      } else {
        attachment.marked = false
        attachments = attachments
      }
    })
  }

  onMount(async () => {
    getAttachments()
  })
</script>

<h3 class="flex items-center mb-1">
  <Paperclip class="text-gray-400 size-4 mr-1" />
  Attachments
  {#if !newAttachment && editable && !freePlan}
    <button
      data-name="new-attachment"
      class="px-4 ml-4 bg-gray-50 flex border border-gray-200 text-gray-800 hover:bg-gray-100 rounded-sm shadow-sm text-xs py-1"
      onclick={() => {
        newAttachment = true
      }}
    >
      <Plus class="size-4 mr-1 text-gray-500" />Add File Attachment
    </button>
  {/if}
</h3>

{#if freePlan}
  <div
    class="mt-6 text-left p-4 border border-yellow-200 bg-yellow-50 text-yellow-800 rounded-lg"
  >
    <Lock class="size-6 text-yellow-700 inline" />
    The <span class="italic">Attachments</span> feature is not available on the
    free plan.
    <a
      href={`/plans/${appStore.plan.id}/admin`}
      class="font-bold hover:underline hover:text-blue-600"
      >Please upgrade to a Standard Subscription</a
    > to view and add file attachments to your goals and tasks.
  </div>
{:else if newAttachment}
  <Form
    attachment={{ plan_id: plan.id, attachable_type: type, attachable_id: id }}
    operation="create"
    oncreate={(attachment) => {
      attachments = [attachment, ...attachments]
      newAttachment = false
      toast("File Attachment added", "success")
    }}
    oncancel={() => {
      newAttachment = false
    }}
  />
{:else if editAttachment}
  <Form
    {attachment}
    operation="update"
    oncreate={(attachment) => {
      attachments = [attachment, ...attachments]
      editAttachment = false
      toast("File Attachment updated", "success")
    }}
    oncancel={() => {
      editAttachment = false
    }}
  />
{:else}
  <div class="overflow-y-auto" data-name="progresses">
    {#if loading}
      <div class="py-12 flex justify-center w-full">
        <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
      </div>
    {:else}
      <div class="mt-8 flex flex-col">
        <div class="min-w-full py-2 align-middle">
          <div class="shadow border border-gray-200">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >Name</th
                  >
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >File Name</th
                  >
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  ></th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span class="sr-only">Delete</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                {#each attachments as attachment}
                  <tr>
                    <td
                      class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                      >{attachment.name}</td
                    >
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {attachment.filename}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      <a
                        class="text-blue-800 bg-blue-50 hover:bg-blue-100 shadow py-1 px-3 border border-rounded border-gray-200 inline-flex justify-start items-center"
                        rel="noopener noreferrer"
                        href={attachment.url}
                        target="_blank"
                      >
                        <Download class="text-blue-500 size-4 mr-1" />
                        Download</a
                      >
                    </td>
                    <td
                      class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                    >
                      {#if editable}
                        <button
                          onclick={() => deleteAttachment(attachment)}
                          class="text-red-800 bg-red-50 hover:bg-red-100 py-1 shadow px-3 border border-rounded border-gray-200 inline-flex justify-start items-center"
                        >
                          <Trash2 class="text-red-500 size-4 mr-1" />
                          Delete
                        </button>
                      {/if}
                    </td>
                  </tr>
                {:else}
                  <tr>
                    <td colspan="4" class="py-3 text-center font-base">
                      No files attached
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    {/if}
  </div>
{/if}
